import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import classNames from 'clsx'
import colorUtil from 'color'
import memoizeOne from 'memoize-one'
import PropTypes from 'prop-types'
import { omit, pick } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import {
  ButtonBase,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

import { Allowed, allowedProps, Can } from '~/src/Flags'
import createLogger from '~/src/Lib/Logging'
import { datePropType, renderableType } from '~/src/Lib/PropTypes'
import { getDateTime, noop, usePrevious } from '~/src/Lib/Utils'
import Head from '~/src/UI/Shared/Head'
import IconRenderer from '~/src/UI/Shared/Icon'

import { annotationTypes as defaultAnnotationTypes } from './constants'
import { useAllowedAnnotationTypes } from './utils'

const getButtonPermissions = ({ actions }) => actions.filter(action => action.startsWith('change_'))
const SPEED_DIAL_CHANGE_DEBOUNCE_THRESHOLD = 50
const displayName = 'AddButton'
const logger = createLogger(displayName)

const selector = '[data-speed-dial], [role=dialog]'

const getTooltipClasses = memoizeOne(pick(['tooltip']))

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    '& > [class*=MuiSpeedDial-fab]': {
      width: theme.spacing(5),
      height: theme.spacing(5),
      minHeight: 'auto',
    },
    '& [class*=MuiSpeedDialIcon-root]': {
      height: 18,
    },
    '& [class*=MuiSvgIcon-root]': {
      fontSize: 18,
    },
    '& [class*=MuiSpeedDialAction-staticTooltip]:hover': {
      cursor: 'pointer',
      '& [class*=MuiSpeedDialAction-fab], & [class*=MuiSpeedDialAction-staticTooltipLabel]': {
        backgroundColor: colorUtil(theme.palette.background.default)
          .mix(colorUtil(theme.palette.primary.main), theme.palette.states.hovered)
          .string(),
      }
    },
    '& [class*=MuiSpeedDialAction-staticTooltipLabel]': {
      whiteSpace: 'nowrap',
      color: theme.palette.primary.main,
    },
    '& [class*=MuiSpeedDialAction-fab]:not([class*=MuiSpeedDialAction-fabClosed])': {
      width: theme.spacing(4.5),
      height: theme.spacing(4.5),
      minHeight: 'auto',
    },
    '&:before': {
      backdropFilter: 'none'
    },
    '&$open:not($noBackdrop):before': {
      position: 'fixed',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      content: '""',
      backdropFilter: 'blur(3px) brightness(70%)',
      pointerEvents: 'none',
    },
  },
  noBackdrop: {},
  open: {},
  tooltip: {
    padding: theme.spacing(0.5, 2),
    background: theme.palette.background.default,
    color: theme.palette.primary.main,
    boxShadow: theme.shadows[1],
    fontSize: 14,
    lineHeight: 1.55,
    letterSpacing: 0.5,
  },
}), { name: displayName })

const speedDialPropsPicker = pick([
  'direction',
  'hidden',
  'icon',
  'onClose',
  'onOpen',
  'open',
  'openIcon',
])

const isBlocked = () => Boolean(document.querySelector('[class*=MuiPopover-root]:not([aria-hidden]):not([style*="pointer-events: none"]), [class*=MuiDialog-root]'))

const clickAwayEvents = ['click', 'touchstart']
const navEvents = ['mousedown', 'touchstart']
const listenerOptions = Object.freeze({ passive: true })

const getButtonProps = memoizeOne(({ annotationType: { schema }, timestamp }) => ({
  component: 'a',
  href: timestamp ? `#/${schema}/new/${getDateTime(timestamp).toMillis()}` : `#/${schema}/new`,
}))

const allowedPropsPicker = pick(allowedProps)
const allowedPropsOmitter = omit(allowedProps)

const CanAction = props => {
  const { annotationType, classes, dataTestId, index, tooltipOpen, tooltipPlacement, onTypeSelect, ...passthru } = props
  const { Icon, type, label = type } = annotationType
  const buttonProps = getButtonProps(props)
  const propsForAllowed = allowedPropsPicker(annotationType)
  const actionPermissions = getButtonPermissions(annotationType)

  return (
    <Allowed {...propsForAllowed} actions={actionPermissions}>
      <SpeedDialAction
        {...allowedPropsOmitter(passthru)}
        data-testid={`${dataTestId}-${type}`}
        FabProps={buttonProps}
        icon={<IconRenderer icon={Icon} />}
        TooltipClasses={getTooltipClasses(classes)}
        tooltipOpen={tooltipOpen}
        tooltipPlacement={tooltipPlacement}
        tooltipTitle={(
          <ButtonBase {...buttonProps} color="primary">
            <Typography color="inherit">{typeof label === 'string' ? label : type}</Typography>
          </ButtonBase>
        )}
      />
    </Allowed>
  )
}

export const AddButtonComponent = ({
  annotationTypes = defaultAnnotationTypes.filter(({ readonly }) => !readonly),
  className = '',
  dataTestId = 'add-annotation',
  timestamp = null,
  dialogIsOpen,
  noBackdrop = false,
  opener,
  openOnHover = false,
  radial = false,
  tooltipOpen = false,
  tooltipPlacement = 'right',
  onClick = noop,
  onComplete = noop,
  onOpen: propsOnOpen = noop,
  onClose: propsOnClose = noop,
  ...passThruProps
}) => {
  const classes = useStyles(passThruProps)
  const [open, setOpen] = useState(false)
  const prevDialogIsOpen = usePrevious(dialogIsOpen)
  const allowedAnnotationTypes = useAllowedAnnotationTypes(annotationTypes)
  const tierAnnotationTypes = annotationTypes.filter(annotation => allowedAnnotationTypes.has(annotation.type))

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => onComplete(), [])

  const speedDialChangeRef = useRef(0)

  const onToggle = useCallback(event => {
    const now = Date.now()
    if (isBlocked() || speedDialChangeRef.current > (now - SPEED_DIAL_CHANGE_DEBOUNCE_THRESHOLD)) {
      logger.warn('+ button blocked or debounced:', { blocked: isBlocked(), now, last: speedDialChangeRef.current })
      return
    }

    speedDialChangeRef.current = now

    event.nativeEvent?.stopPropagation()
    event.nativeEvent?.stopImmediatePropagation?.()
    if (typeof onClick === 'function') onClick()
    setOpen(old => {
      if (old) {
        if (typeof propsOnClose === 'function') propsOnClose()
        if (typeof onComplete === 'function') onComplete()
      } else if (typeof propsOnOpen === 'function') {
        propsOnOpen()
      }
      return !old
    })
  }, [setOpen, onClick, onComplete, propsOnClose, propsOnOpen])
  const openTimeout = useRef(null)
  const onOpen = useCallback(() => {
    const now = Date.now()
    if (isBlocked() || speedDialChangeRef.current > (now - SPEED_DIAL_CHANGE_DEBOUNCE_THRESHOLD)) {
      logger.warn('+ button blocked or debounced:', { blocked: isBlocked(), now, last: speedDialChangeRef.current })
      return
    }

    speedDialChangeRef.current = now

    openTimeout.current = setTimeout(() => {
      setOpen(true)
      openTimeout.current = null
    }, 350)
  }, [setOpen])

  const onMouseOut = useCallback(() => {
    if (openTimeout.current) {
      clearTimeout(openTimeout.current)
      openTimeout.current = null
    }
  }, [openTimeout])

  const onClose = useCallback(() => {
    const now = Date.now()
    if (speedDialChangeRef.current > (now - SPEED_DIAL_CHANGE_DEBOUNCE_THRESHOLD)) {
      return
    }

    speedDialChangeRef.current = now
    setOpen(false)
    onComplete()
  }, [onComplete, setOpen])

  useEffect(() => {
    const clickAwayListener = ({ target }) => {
      if (target && !target.closest(selector) && target.id !== 'root') {
        onClose()
      }
    }

    if (!document?.body) return undefined

    clickAwayEvents.forEach(eventName => {
      if (!document?.body) return
      document.body.addEventListener(eventName, clickAwayListener, listenerOptions)
    })

    return () => clickAwayEvents.forEach(eventName => {
      if (!document?.body) return
      document.body.removeEventListener(eventName, clickAwayListener)
    })
  }, [onClose])

  const speedDialHandler = useCallback(event => {
    const { target } = event
    if (target) {
      const link = target.closest('a[href]') ?? document.querySelector(`[aria-describedby="${target.id}"]`)
      if (link && link.href) {
        onClick?.()
      }
    }
  }, [onClick])

  useEffect(() => {
    document.querySelectorAll(`[data-testid=${dataTestId}] [id*=addannotation-action-] > * `)
      .forEach(a => navEvents.forEach(e => a.addEventListener(e, speedDialHandler, listenerOptions)))
    return () => document.querySelectorAll(`[data-testid=${dataTestId}] [id*=addannotation-action-] > *`)
      .forEach(a => navEvents.forEach(e => a.removeEventListener(e, speedDialHandler)))
  }, [dataTestId, open, speedDialHandler])

  useEffect(() => {
    if (!open) return
    if (prevDialogIsOpen && !dialogIsOpen) {
      onClose()
    }
  }, [open, dialogIsOpen, prevDialogIsOpen, onClose])
  const allPermissions = tierAnnotationTypes.flatMap(getButtonPermissions)

  return (
    <Can any actions={allPermissions}>
      <SpeedDial
        {...speedDialPropsPicker(passThruProps)}
        ariaLabel="add annotation"
        className={classNames({
          [classes.root]: true,
          [classes.noBackdrop]: noBackdrop,
          [classes.open]: open,
          [className]: Boolean(className),
          [classes.radial]: radial,
        })}
        data-testid={dataTestId}
        data-speed-dial="true"
        icon={<SpeedDialIcon />}
        onClick={openOnHover ? noop : onToggle}
        onOpen={openOnHover ? onOpen : propsOnOpen}
        onClose={openOnHover ? onClose : propsOnClose}
        onMouseLeave={onMouseOut}
        open={open}
      >
        {tierAnnotationTypes.map(annotationType => (
          <CanAction
            dataTestId={dataTestId}
            timestamp={timestamp}
            key={annotationType.type}
            annotationType={annotationType}
            classes={classes}
            tooltipOpen={tooltipOpen}
            tooltipPlacement={tooltipPlacement}
            onClick={onClick}
          />
        ))}
      </SpeedDial>
      {open ? <Head><body className="speed-dial__open" data-speed-dial-opener={opener} /></Head> : null}
    </Can>
  )
}
AddButtonComponent.propTypes = {
  annotationTypes: PropTypes.arrayOf(PropTypes.shape({
    Icon: renderableType,
    type: PropTypes.string,
    label: PropTypes.node,
    model: PropTypes.string
  })),
  dataTestId: PropTypes.string,
  timestamp: datePropType,
  dialogIsOpen: PropTypes.bool.isRequired,
  noBackdrop: PropTypes.bool,
  opener: PropTypes.string.isRequired,
  openOnHover: PropTypes.bool,
  radial: PropTypes.bool,
  tooltipOpen: PropTypes.bool,
  tooltipPlacement: PropTypes.string,
  onClick: PropTypes.func,
  onClose: PropTypes.func,
  onComplete: PropTypes.func,
  onOpen: PropTypes.func,
}
AddButtonComponent.displayName = displayName

export default React.memo(props => {
  const { dialogIsOpen } = useConnect('selectDialogIsOpen')

  return <AddButtonComponent {...props} dialogIsOpen={dialogIsOpen} />
})
